const RAMP = 0.05; // seconds for gain fade

/**
 * Routes the AI WebRTC stream through an AudioContext gain node so it can be
 * muted instantly when museum audio starts playing.
 *
 * Museum audio (HTMLAudioElement / new Audio()) plays directly — routing it
 * through AudioContext requires CORS headers on the audio origin (S3), which
 * we can't guarantee. Instead we control overlap by muting the AI side.
 *
 *   AudioContext
 *     └── aiGain ← WebRTC remote stream → destination (speakers)
 *                ↑ also tapped by _analyser (passive, not in playback path)
 */
class AudioMixer {
  constructor() {
    this.context = new (window.AudioContext || window.webkitAudioContext)();
    this.aiGain = this.context.createGain();
    this.aiGain.connect(this.context.destination);
    this._aiSource = null;

    // Passive analyser tap — not in the playback path, used only for silence detection
    this._analyser = this.context.createAnalyser();
    this._analyser.fftSize = 256;
  }

  /** Call after user gesture to satisfy browser autoplay policy. */
  resume() {
    if (this.context.state === 'suspended') this.context.resume();
  }

  /** Connect the WebRTC remote MediaStream to the AI gain node. */
  connectAIStream(stream) {
    if (this._aiSource) {
      try { this._aiSource.disconnect(); } catch (_) {}
    }
    this._aiSource = this.context.createMediaStreamSource(stream);
    this._aiSource.connect(this.aiGain);
    this._aiSource.connect(this._analyser); // passive tap for silence detection
  }

  /**
   * Resolves when the AI stream goes silent for `silenceMs` consecutive ms
   * after having produced sound. Language and speed agnostic.
   */
  waitForSilence({ threshold = 0.008, silenceMs = 800, maxWaitMs = 60000 } = {}) {
    return new Promise(resolve => {
      const buf = new Float32Array(this._analyser.fftSize);
      const POLL = 80;
      let silenceStart = null;
      let seenActivity = false;
      const deadline = Date.now() + maxWaitMs;

      const check = () => {
        this._analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);

        if (rms >= threshold) {
          seenActivity = true;
          silenceStart = null;
        } else if (seenActivity) {
          if (silenceStart === null) silenceStart = Date.now();
          if (Date.now() - silenceStart >= silenceMs) { resolve(); return; }
        }

        if (Date.now() >= deadline) { resolve(); return; }
        setTimeout(check, POLL);
      };

      check();
    });
  }

  muteAI()   { this._ramp(this.aiGain, 0); }
  unmuteAI() { if (!this._aiDisabled) this._ramp(this.aiGain, 1); }

  /** Permanently silence the AI speaker path (LITE avatar mode: the voice
   *  plays through the avatar's LiveKit track instead). The analyser tap
   *  keeps working so waitForSilence() stays functional. */
  disableAI() {
    this._aiDisabled = true;
    this.aiGain.gain.cancelScheduledValues(this.context.currentTime);
    this.aiGain.gain.setValueAtTime(0, this.context.currentTime);
  }

  /** Undo disableAI() (LITE avatar failed — the voice falls back to the
   *  speakers). Leaves the gain alone; call unmuteAI() to make it audible. */
  enableAI() {
    this._aiDisabled = false;
  }

  _ramp(gainNode, value) {
    gainNode.gain.cancelScheduledValues(this.context.currentTime);
    gainNode.gain.linearRampToValueAtTime(value, this.context.currentTime + RAMP);
  }

  destroy() {
    try { this.context.close(); } catch (_) {}
  }
}

export default AudioMixer;
